/**
 * Service (Application Pillar) Validation Schemas
 */

import { z } from 'zod';
import { nameField, optionalBoundedText, optionalUrlField } from './shared';

/**
 * Common validation rules for Service entities
 */
export const serviceValidationRules = {
  /** Service name validation (1-200 characters) */
  name: nameField(200, 'Service name is required', 'Service name must be less than 200 characters'),

  /** Short name validation (lowercase, hyphens, alphanumeric) — optional (used for updates) */
  shortName: z
    .string()
    .regex(/^[a-z0-9-]+$/, 'Short name must be lowercase letters, numbers, and hyphens only')
    .optional(),

  /** Short name validation — required at create time (URL-friendly service identifier) */
  shortNameRequired: z
    .string()
    .min(1, 'Short name is required')
    .regex(/^[a-z0-9-]+$/, 'Short name must be lowercase letters, numbers, and hyphens only'),

  /** Description validation (optional, max 1000 characters) */
  description: optionalBoundedText(1000, 'Description must be less than 1000 characters'),

  /** Repository URL validation (optional, must be valid URL) */
  repo: optionalUrlField('Repository'),

  /** Website URL validation (optional, must be valid URL) — identity of a `Website` service (issue #1287) */
  url: optionalUrlField('Website URL'),

  /** Team ID validation (must start with "team-") */
  teamId: z
    .string()
    .min(1, 'Team is required')
    .regex(/^team-/, 'Team ID must start with "team-"'),
};

/**
 * Full Service create form schema
 */
export const createServiceSchema = z.object({
  name: serviceValidationRules.name,
  shortName: serviceValidationRules.shortNameRequired,
  description: serviceValidationRules.description,
  serviceType: z.enum(['Application', 'Integration', 'Platform', 'Infrastructure', 'Website'], {
    message: 'Service type is required',
  }),
  teamId: serviceValidationRules.teamId,
  repo: serviceValidationRules.repo,
  url: serviceValidationRules.url,
  // 'unassessed'/'unassigned' (issue #1525, ADR-1319/1525) are real, honest
  // "pending" states a scanner-ingested service may already carry — the form
  // must round-trip them, not just the human-assessed values.
  lifecycleStatus: z
    .enum(['prototype', 'development', 'production', 'deprecated', 'retired', 'unassessed'])
    .optional(),
  tier: z.enum(['tier0', 'tier1', 'tier2', 'tier3', 'unassigned'], {
    message: 'Tier is required',
  }),
});

/**
 * Service edit form schema — same as create except `teamId`, which an
 * already-existing unowned service may legitimately have empty. Ownership
 * changes go through claim/assign-team (issue #536), not this generic edit
 * path, so this form must not force a team assignment just to save an
 * unrelated field. Kept distinct from `updateServiceSchema` below (a
 * PATCH-style schema where every field is optional): `ServiceForm` always
 * submits the complete field set in both create and edit mode, so only
 * `teamId`'s requirement needs to differ.
 */
export const editServiceSchema = createServiceSchema.extend({
  teamId: z.union([serviceValidationRules.teamId, z.literal('')]),
});

/**
 * Service update form schema (all fields optional except at least one must be present)
 */
export const updateServiceSchema = z
  .object({
    name: serviceValidationRules.name.optional(),
    shortName: serviceValidationRules.shortName,
    description: serviceValidationRules.description,
    serviceType: z
      .enum(['Application', 'Integration', 'Platform', 'Infrastructure', 'Website'])
      .optional(),
    teamId: serviceValidationRules.teamId.optional(),
    repo: serviceValidationRules.repo,
    url: serviceValidationRules.url,
    lifecycleStatus: z
      .enum(['prototype', 'development', 'production', 'deprecated', 'retired', 'unassessed'])
      .optional(),
    tier: z.enum(['tier0', 'tier1', 'tier2', 'tier3', 'unassigned']).optional(),
  })
  .refine((data) => Object.values(data).some((value) => value !== undefined), {
    message: 'At least one field must be updated',
  });

/**
 * Type inference for create service form
 */
export type CreateServiceFormData = z.infer<typeof createServiceSchema>;

/**
 * Type inference for update service form
 */
export type UpdateServiceFormData = z.infer<typeof updateServiceSchema>;
